import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Fade,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  Lock as LockIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  Factory as ProductionIcon
} from '@mui/icons-material';
import SalesData from './components/SalesData';
import ProductData from './components/ProductData';
import ProductionData from './components/ProductionData';
import FormulationsData from './components/FormulationsData';
import InventoryData from './components/InventoryData';
import Navigation from './components/Navigation';

const DashboardPage = () => {
  const { companyId } = useParams();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('sales');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchCompanyDetails();
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [companyId, fetchData]);

  const fetchCompanyDetails = async () => {
    try {
      // Get the user's actual company (RLS automatically filters by user)
      const { data, error } = await supabase
        .from('companies')
        .select('name, id');

      if (error) throw error;

      if (data && data.length > 0) {
        const company = data[0];
        // SECURITY CHECK: Verify URL company ID matches user's actual company
        if (company.id !== companyId) {
          // Redirect to the user's actual company dashboard
          window.location.href = `/company/${company.id}/dashboard`;
          return;
        }
        
        // If we get here, user owns the company in the URL
        setCompanyName(company.name);
      } else {
        // No company data available - might be during transition
        setCompanyName('Loading...');
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  // Permission check - Dashboard accessible to users with dashboard.view permission
  if (!hasPermission('dashboard.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, #4CAF50, ${alpha('#4CAF50', 0.4)})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <LockIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Dashboard Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access this dashboard. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#4CAF50', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#4CAF50', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#4CAF50', 0.1),
                mb: 2
              }}>
                <DashboardIcon sx={{ fontSize: 48, color: '#4CAF50' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#4CAF50',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Dashboard
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing business metrics and performance data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#4CAF50',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'error.main',
            boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.15)}`
          }}
        >
          {error}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />} 
          onClick={fetchData}
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.5,
            fontSize: '0.9rem',
            fontWeight: 500,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
            '&:hover': {
              border: `1px solid ${theme.palette.primary.main}`,
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
        >
          Try Again
        </Button>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Card sx={{
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              borderTop: '3px solid #4CAF50',
              background: theme.palette.grey[100],
              boxShadow: 'none',
              transition: 'all 0.2s ease',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${alpha('#4CAF50', 0.18)}`,
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(${alpha('#4CAF50', 0.1)}, ${alpha('#4CAF50', 0.05)})`,
                    border: `1px solid ${alpha('#4CAF50', 0.2)}`
                  }}>
                    <DashboardIcon sx={{ fontSize: 24, color: '#4CAF50' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{
                      fontWeight: 500,
                      color: '#4CAF50',
                      mb: 0.5,
                      fontSize: '1.8rem'
                    }}>
                      Dashboard
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      mb: 1
                    }}>
                      {companyName || 'R&R Cosmetics LLC'}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '0.9rem',
                      lineHeight: 1.4
                    }}>
                      Monitor key performance metrics, financial data, and system activity across your enterprise 
                      with real-time analytics
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Main Dashboard Content with Navigation */}
          <Box sx={{ display: 'flex', gap: 4, mt: 10 }}>
            {/* Navigation Sidebar */}
            <Navigation 
              activeSection={activeSection} 
              onSectionChange={setActiveSection} 
            />

            {/* Dashboard Content */}
            <Box sx={{ flex: 1 }}>
          {/* Sales Dashboard Section */}
              {activeSection === 'sales' && (
                <Box id="sales" sx={{ mb: 8 }}>
                  <Box id="sales-metrics" sx={{ 
                    mb: 4, 
                    pb: 2, 
                    borderBottom: `1px solid ${theme.palette.grey[300]}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrendingUpIcon sx={{ fontSize: 32, color: '#2196F3', mr: 2 }} />
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: '#2196F3', 
                        fontSize: '2rem',
                        letterSpacing: '-0.3px'
                      }}>
                        Sales Analytics
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '1.1rem'
                    }}>
                      Revenue trends, customer insights, and sales performance metrics
                    </Typography>
                  </Box>
                  <SalesData companyId={companyId} />
                </Box>
              )}

              {/* Product Analytics Section */}
              {activeSection === 'products' && (
                <Box id="products" sx={{ mb: 8 }}>
                  <Box id="product-metrics" sx={{ 
                    mb: 4, 
                    pb: 2, 
                    borderBottom: `1px solid ${theme.palette.grey[300]}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AssessmentIcon sx={{ fontSize: 32, color: '#4CAF50', mr: 2 }} />
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: '#4CAF50', 
                        fontSize: '2rem',
                        letterSpacing: '-0.3px'
                      }}>
                        Product Analytics
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '1.1rem'
                    }}>
                      Best-selling products, inventory insights, and performance analysis
                    </Typography>
                  </Box>
                  <ProductData companyId={companyId} />
                </Box>
              )}

              {/* Production Analytics Section */}
              {activeSection === 'production' && (
                <Box id="production" sx={{ mb: 8 }}>
                  <Box id="production-metrics" sx={{ 
                    mb: 4, 
                    pb: 2, 
                    borderBottom: `1px solid ${theme.palette.grey[300]}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ProductionIcon sx={{ fontSize: 32, color: '#E91E63', mr: 2 }} />
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: '#E91E63', 
                        fontSize: '2rem',
                        letterSpacing: '-0.3px'
                      }}>
                        Production Analytics
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '1.1rem'
                    }}>
                      Batch production, line runs, quality control, costs, and manufacturing efficiency
                    </Typography>
                  </Box>
                  <ProductionData companyId={companyId} />
                </Box>
              )}

              {/* Formulation Analytics Section */}
              {activeSection === 'formulations' && (
                <Box id="formulations" sx={{ mb: 8 }}>
                  <Box id="formulation-metrics" sx={{ 
                    mb: 4, 
                    pb: 2, 
                    borderBottom: `1px solid ${theme.palette.grey[300]}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ScienceIcon sx={{ fontSize: 32, color: '#9C27B0', mr: 2 }} />
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: '#9C27B0', 
                        fontSize: '2rem',
                        letterSpacing: '-0.3px'
                      }}>
                        Formulation Analytics
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '1.1rem'
                    }}>
                      R&D insights, ingredient usage, cost analysis, and development progress
                    </Typography>
                  </Box>
                  <FormulationsData companyId={companyId} />
                </Box>
              )}

              {/* Inventory Analytics Section */}
              {activeSection === 'inventory' && (
                <Box id="inventory" sx={{ mb: 8 }}>
                  <Box id="inventory-metrics" sx={{ 
                    mb: 4, 
                    pb: 2, 
                    borderBottom: `1px solid ${theme.palette.grey[300]}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InventoryIcon sx={{ fontSize: 32, color: '#FF5722', mr: 2 }} />
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: '#FF5722', 
                        fontSize: '2rem',
                        letterSpacing: '-0.3px'
                      }}>
                        Inventory Analytics
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontSize: '1.1rem'
                    }}>
                      Stock levels, costs, supplier data, and inventory performance across all categories
                    </Typography>
                  </Box>
                  <InventoryData companyId={companyId} />
                </Box>
              )}
                                    </Box>
          </Box>

        </Container>
      </Box>
    </Fade>
  );
};

export default DashboardPage;